<template>
  <div
    v-if="show"
    class="bulk-complete-notification"
    :class="[
      runningRows.length > 0
        ? 'bulk-complete-notification--running'
        : 'bulk-complete-notification--completed',
      { 'bulk-complete-notification--clickable': targetDate },
    ]"
    role="button"
    tabindex="0"
    @click="goToClosing"
    @keydown.enter="goToClosing"
    @keydown.space.prevent="goToClosing"
  >
    <!-- 実行中（複数走っていれば複数行） -->
    <template v-if="runningRows.length > 0">
      <div
        v-for="row in runningRows"
        :key="row.type"
        class="bulk-complete-notification__row"
      >
        <span class="bulk-complete-notification__icon">
          <v-progress-circular
            indeterminate
            size="22"
            width="3"
            color="white"
          />
        </span>
        <span class="bulk-complete-notification__label">{{ labelFor(row.type) }}を実行中…</span>
      </div>
    </template>

    <!-- 全部完了 -->
    <div
      v-else-if="completedRows.length > 0"
      class="bulk-complete-notification__row"
    >
      <span class="bulk-complete-notification__icon">
        <v-icon
          color="white"
          size="26"
        >{{ icons.mdiCheckCircle }}</v-icon>
      </span>
      <span class="bulk-complete-notification__label">クロージング集計完了</span>
      <button
        type="button"
        class="bulk-complete-notification__close"
        aria-label="閉じる"
        @click.stop="dismissAll"
      >
        ×
      </button>
    </div>
  </div>
</template>

<script>
import { computed } from '@vue/composition-api'
import { mdiCheckCircle } from '@mdi/js'
import useGlobalBulkCompletion from '@/composables/useGlobalBulkCompletion'

// 表示する型のラベル
const TYPE_LABEL = {
  EodClosingUserAggregation: 'ユーザー集計',
  EodClosingTableAggregation: '卓集計',
  EodClosingClubAggregation: '店舗集計',
}

// 集計タイプ -> クロージング画面のステップ key（?step= に渡す）
const TYPE_TO_STEP_KEY = {
  EodClosingUserAggregation: 'user',
  EodClosingTableAggregation: 'table',
  EodClosingClubAggregation: 'club',
}

// クロージング画面（新クロージング）に居る間は通知を出さない。
// 画面内で進捗が見えるため重複させない。
const IS_CLOSING_ROUTE_PATTERN = /\/eod-closing-v2(\/|$|\?)/

export default {
  name: 'GlobalBulkCompleteNotification',
  setup(_, { root }) {
    const { visibleNotifications, dismissAll, dismissCompleted } = useGlobalBulkCompletion()

    const labelFor = type => TYPE_LABEL[type] || type

    const isOnClosingRoute = computed(() => IS_CLOSING_ROUTE_PATTERN.test(root.$route.path))

    const runningRows = computed(() => (
      visibleNotifications.value.filter(row => row.status === 'in_progress')
    ))

    const completedRows = computed(() => (
      visibleNotifications.value.filter(row => row.status === 'completed')
    ))

    const show = computed(() => (
      !isOnClosingRoute.value && (runningRows.value.length > 0 || completedRows.value.length > 0)
    ))

    // 表示中の通知の中から、対象営業日を 1 つ拾う。
    // 同時実行されている型は同じ日付に対して起動されているはずなので最初に見つけた date を採用。
    const targetDate = computed(() => (
      visibleNotifications.value.map(row => row.date).find(date => !!date) || null
    ))

    // 遷移先のステップ key（最初の通知の集計タイプから決定）。
    const targetStepKey = computed(() => (
      TYPE_TO_STEP_KEY[visibleNotifications.value[0]?.type] || null
    ))

    const goToClosing = () => {
      // dismiss 処理で computed が変化する前に、遷移先を先に退避する。
      const date = targetDate.value
      const stepKey = targetStepKey.value
      if (!date) return
      // 完了済みの通知だけ閉じる。実行中の通知は残し、クロージングに遷移しても
      // （まだ集計中なら）他画面に移動したときに再表示されるようにする。
      dismissCompleted()
      const query = { date }
      if (stepKey) query.step = stepKey
      root.$router.push({ name: 'eod-closing-v2', query })
    }

    return {
      show,
      runningRows,
      completedRows,
      labelFor,
      dismissAll,
      targetDate,
      goToClosing,
      icons: { mdiCheckCircle },
    }
  },
}
</script>

<style lang="scss" scoped>
.bulk-complete-notification {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  color: #fff;
  border-radius: 14px;
  padding: 18px 24px;
  min-width: 320px;
  max-width: 460px;
  outline: none;
  transition: transform .15s ease, box-shadow .15s ease, filter .15s ease;
}

// 実行中: 目を引くオレンジ系グラデーション + 脈動アニメーション
.bulk-complete-notification--running {
  background-image: linear-gradient(135deg, #ff8a00 0%, #ff5252 100%);
  box-shadow: 0 10px 32px rgba(255, 82, 82, .45);
  animation: bulk-complete-pulse 1.6s ease-in-out infinite;
}

// 完了: はっきりしたグリーン系グラデーション
.bulk-complete-notification--completed {
  background-image: linear-gradient(135deg, #2e7d32 0%, #43a047 100%);
  box-shadow: 0 10px 32px rgba(67, 160, 71, .45);
}

.bulk-complete-notification--clickable {
  cursor: pointer;

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.05);
  }

  &:focus-visible {
    box-shadow: 0 0 0 3px rgba(255, 255, 255, .7), 0 10px 32px rgba(0, 0, 0, .35);
  }
}

@keyframes bulk-complete-pulse {
  0%,
  100% {
    box-shadow: 0 10px 32px rgba(255, 82, 82, .45);
  }

  50% {
    box-shadow: 0 10px 40px rgba(255, 82, 82, .75);
  }
}

.bulk-complete-notification__row {
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.5;

  & + & {
    margin-top: 10px;
  }
}

.bulk-complete-notification__icon {
  display: inline-flex;
  width: 26px;
  height: 26px;
  align-items: center;
  justify-content: center;
}

.bulk-complete-notification__label {
  flex: 1;
}

.bulk-complete-notification__close {
  font-size: 24px;
  line-height: 1;
  color: rgba(255, 255, 255, .85);
  cursor: pointer;
  background: transparent;
  border: none;
  padding: 0 4px;

  &:hover {
    color: #fff;
  }
}
</style>
